import {
  type IncidentReportRiskAssessmentParticipant,
  type IncidentReportRiskAssessmentCore,
  type IncidentReportYesNoOther,
  type IncidentReportNotifiedParty,
  type IncidentReportNotifiedPartyType,
} from "components/CriticalIncident/interfaces";

export const MAX_PARTICIPANTS = 10;
export const PARTICIPANT_NUMBER_OPTIONS = Array.from({ length: MAX_PARTICIPANTS }, (_, i) => i + 1);
export const NAME_MAX_LENGTH = 250;
export const PHONE_MAX_LENGTH = 100;
export const EMAIL_MAX_LENGTH = 100;

// Shared with the AU risk assessment embedded in IndividualSupportSession — the "empty"
// shape for everything RiskFactorsSection/ActionsTakenSection/NotifiedPartyFields touch.
export const EMPTY_RISK_ASSESSMENT_CORE: IncidentReportRiskAssessmentCore = {
  riskOfSuicide: null,
  suicideRiskFactors: [],
  riskOfSelfHarm: null,
  selfHarmRiskFactors: [],
  riskOfHarmToFromOthers: null,
  harmToFromOthersRiskFactors: [],
  riskDescription: "",
  riskFactors: [],
  hasDutyToReport: "" as IncidentReportYesNoOther,
  dutyToReportDescription: "",
  notifiedPartiesDetails: "",
  measureTakenToReduceRisks: "",
  agreementAgainstSuicide: "",
  agreementAgainstSuicideDescription: "",
  selectedNotifiedParties: [],
  partiesNotified: [],
};

export const EMPTY_PARTICIPANT: IncidentReportRiskAssessmentParticipant = {
  name: "",
  dateOfBirth: null,
  phoneNumber: "",
  emailAddress: "",
  ...EMPTY_RISK_ASSESSMENT_CORE,
};

export const RISK_DESCRIPTION_MAX_LENGTH = 2000;
export const ACTIONS_TAKEN_MAX_LENGTH = 2000;
export const NOTIFIED_PARTY_MAX_LENGTH = 1000;
// Dataverse attributes behind the name/phone/etc. fields are limited to 100 characters (DY365-20421)
export const NOTIFIED_PARTY_FIELD_MAX_LENGTH = 100;

export const NOTIFIED_PARTY_TYPES: IncidentReportNotifiedPartyType[] = [
  "clinicalSupervisor",
  "potentialVictim",
  "police",
  "childWelfare",
  "emergencyDepartment",
  "otherAgency",
  "family",
  "workplace",
  "learningInstitution",
  "none",
];

export const NOTIFIED_PARTY_LABEL_KEYS: Record<IncidentReportNotifiedPartyType, string> = {
  clinicalSupervisor: "notified_party_clinical_supervisor",
  potentialVictim: "notified_party_potential_victim",
  police: "notified_party_police",
  childWelfare: "notified_party_child_welfare",
  emergencyDepartment: "notified_party_emergency_department",
  otherAgency: "notified_party_other_agency",
  family: "notified_party_family",
  workplace: "notified_party_workplace",
  learningInstitution: "notified_party_learning_institution",
  none: "notified_party_none",
};

export function makeEmptyNotifiedParty(partyType: IncidentReportNotifiedPartyType): IncidentReportNotifiedParty {
  switch (partyType) {
    case "clinicalSupervisor":
      return { partyType, notifiedDate: null, wereOthersInformed: null };
    case "potentialVictim":
      return { partyType, notifiedDate: null, victimName: "", victimPhone: "", notes: "" };
    case "police":
      return { partyType, notifiedDate: null, officerName: "", policePhone: "", badgeNumber: "", conversationDetails: "" };
    case "childWelfare":
      return { partyType, notifiedDate: null, agencyName: "", representativeName: "", referenceNumber: "", conversationDetails: "" };
    case "emergencyDepartment":
      return { partyType, notifiedDate: null, departmentName: "", notes: "" };
    case "otherAgency":
      return { partyType, agencyName: "", notes: "" };
    case "family":
      return { partyType, notifiedDate: null, memberName: "", relationshipToClient: "", familyPhone: "", conversationDetails: "" };
    case "workplace":
      return { partyType, notifiedDate: null, contactName: "", jobTitle: "", workplacePhone: "", conversationDetails: "" };
    case "learningInstitution":
      return { partyType, notifiedDate: null, contactName: "", institutionDepartment: "", institutionPhone: "", conversationDetails: "" };
    case "none":
    default:
      return { partyType: "none" };
  }
}

export const RISK_FACTOR_LEVEL_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "ID", labelKey: "risk_factor_level_ideation" },
  { value: "IT", labelKey: "risk_factor_level_intent" },
  { value: "PL", labelKey: "risk_factor_level_plan" },
  { value: "PA", labelKey: "risk_factor_level_previous_attempt" },
];

export const RISK_FACTOR_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "ADD", labelKey: "risk_factor_addiction" },
  { value: "CAB", labelKey: "risk_factor_child_abuse" },
  { value: "CHA", labelKey: "risk_factor_childhood_abuse" },
  { value: "FHSV", labelKey: "risk_factor_family_history" },
  { value: "HOPLS", labelKey: "risk_factor_hopelessness" },
  { value: "IMPDJ", labelKey: "risk_factor_impaired_judgement" },
  { value: "LSS", labelKey: "risk_factor_lack_of_social_support" },
  { value: "PHOSP", labelKey: "risk_factor_previous_hospitalization" },
  { value: "RLOSS", labelKey: "risk_factor_recent_loss" },
  { value: "SSO", labelKey: "risk_factor_safety_sensitive_occupation" },
  { value: "PSA", labelKey: "risk_factor_previous_suicide_attempt" },
  { value: "TRA", labelKey: "risk_factor_trauma" },
  { value: "SISA", labelKey: "risk_factor_self_injury_spouse_abuse" },
  { value: "OTH", labelKey: "risk_factor_other" },
];
